import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    DynamicsLinkedService,
    dynamicsDeploymentTypes,
    servicePrincipalCredentialTypes
} from 'app/model/linkedservice/dynamics.linkedservice';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ReferenceAdapter, Credential, dynamicsAuthTypes, AdapterContext, IAttribute } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { ValueAdapter } from '../value.adapter';

export const dynamicsLSProperties = {
    description: '*',
    type: '*',
    connectVia: '*',
    typeProperties: {
        deploymentType: '*',
        serviceUri: '*',
        organizationName: '*',
        hostName: '*',
        port: '*',
        authenticationType: '*',
        username: '*',
        password: 'secret',
        servicePrincipalId: '*',
        servicePrincipalCredentialType: '*',
        servicePrincipalCredential: 'secret',
        encryptedCredential: '*',
        credential: '*',
        domain: '*'
    },
    parameters: '*'
};
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DynamicsLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: dynamicsLSProperties
    }
})
export class DynamicsLinkedServiceAdapter extends LinkedServiceAdapter<DynamicsLinkedService> {
    protected supportFirstClassParameterExperience = true;
    protected modelConstructor(): new () => DynamicsLinkedService {
        return DynamicsLinkedService;
    }

    protected model(): IModel {
        return DynamicsLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: DynamicsLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.DynamicsLinkedService;
        properties.type = Metamodel.modelFor(DynamicsLinkedService).name;
        return dynamicsPropertiesToResourceAsync(
            linkedService,
            properties,
            linkedServiceResource,
            GlobalVariables.enableConnectorUserAuthCred,
            this.context,
            this.valueToResource.bind(this)
        );
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: DynamicsLinkedService
    ): Promise<DynamicsLinkedService> {
        const properties = linkedServiceResource.properties as Model.DynamicsLinkedService;
        const typeProperties: Model.DynamicsLinkedServiceTypeProperties = properties.typeProperties;
        return dynamicsPropertiesToModelAsync(typeProperties, linkedService, this.context);
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type valueToResourceFn = (obj: Object, attribute: IAttribute) => any;
export const dynamicsPropertiesToResourceAsync = async (
    linkedService: DynamicsLinkedService,
    properties: Model.DynamicsLinkedService,
    linkedServiceResource: Model.LinkedServiceResource,
    ff4UserAuth = true,
    context: AdapterContext,
    valueToResource: valueToResourceFn
): Promise<Model.LinkedServiceResource> => {
    let username;
    let password;
    let serviceUri;
    let organizationName;
    let hostName;
    let port;
    let servicePrincipalCredentialType;
    let servicePrincipalId;
    let servicePrincipalCredential;
    let credential;
    let domain;

    if (linkedService.deploymentType === dynamicsDeploymentTypes.Online) {
        serviceUri = valueToResource(linkedService, DynamicsLinkedService.SERVICE_URL);
        if (linkedService.authenticationType === dynamicsAuthTypes.Office365) {
            username = valueToResource(linkedService, DynamicsLinkedService.USER_NAME);
            password = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                DynamicsLinkedService.PASSWORD,
                context
            ) as Model.SecretBase;
        } else if (linkedService.authenticationType === dynamicsAuthTypes.AADServicePrincipal) {
            servicePrincipalId = valueToResource(linkedService, DynamicsLinkedService.SERVICE_PRINCIPLE_ID);
            servicePrincipalCredentialType = linkedService.servicePrincipalCredentialType;
            if (servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey) {
                servicePrincipalCredential = parameterizablePasswordOrAKVtoSecretBase(
                    linkedService,
                    DynamicsLinkedService.SERVICE_PRINCIPLE_KEY,
                    context
                );
            } else if (servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                servicePrincipalCredential = parameterizablePasswordOrAKVtoSecretBase(
                    linkedService,
                    DynamicsLinkedService.SERVICE_PRINCIPLE_CERT,
                    context
                );
            }
        } else if (
            linkedService.authenticationType === dynamicsAuthTypes.ManagedIdentity ||
            (ff4UserAuth && linkedService.authenticationType === dynamicsAuthTypes.UserAuth)
        ) {
            if (linkedService.credential) {
                credential = ReferenceAdapter.toResource(linkedService.credential, context) as Model.CredentialReference;
            }
        }
    } else if (linkedService.deploymentType === dynamicsDeploymentTypes.OnPremisesWithIfd) {
        if (linkedService.authenticationType === dynamicsAuthTypes.ADAuth) {
            domain = valueToResource(linkedService, DynamicsLinkedService.DOMAIN);
        }
        organizationName = valueToResource(linkedService, DynamicsLinkedService.ORGANIZATION_NAME);
        hostName = valueToResource(linkedService, DynamicsLinkedService.HOST_NAME);
        username = valueToResource(linkedService, DynamicsLinkedService.USER_NAME);
        port = ValueAdapter.isTemplated(linkedService, DynamicsLinkedService.PORT)
            ? valueToResource(linkedService, DynamicsLinkedService.PORT)
            : Number(linkedService.port);
        password = parameterizablePasswordOrAKVtoSecretBase(
            linkedService,
            DynamicsLinkedService.PASSWORD,
            context
        ) as Model.SecretBase;
    }

    properties.typeProperties = {
        deploymentType: linkedService.deploymentType,
        serviceUri,
        organizationName,
        hostName,
        port,
        authenticationType: linkedService.authenticationType,
        username,
        password,
        servicePrincipalId,
        servicePrincipalCredential: servicePrincipalCredential as Model.SecretBase,
        servicePrincipalCredentialType,
        domain,
        credential
    };
    return linkedServiceResource;
};

export const dynamicsPropertiesToModelAsync = async (
    typeProperties: Model.DynamicsLinkedServiceTypeProperties,
    linkedService: DynamicsLinkedService,
    context: AdapterContext
): Promise<DynamicsLinkedService> => {
    linkedService.deploymentType = typeProperties.deploymentType;
    linkedService.authenticationType = typeProperties.authenticationType;

    ValueAdapter.toModel(linkedService, DynamicsLinkedService.SERVICE_URL, typeProperties.serviceUri);
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.ORGANIZATION_NAME, typeProperties.organizationName);
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.PORT, typeProperties.port);
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.USER_NAME, typeProperties.username);
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.HOST_NAME, typeProperties.hostName);
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.DOMAIN, typeProperties.domain);
    parameterizableSecretBaseToModel(
        linkedService,
        DynamicsLinkedService.PASSWORD,
        typeProperties.password,
        context,
        linkedService.deploymentType === dynamicsDeploymentTypes.OnPremisesWithIfd ||
        linkedService.authenticationType === dynamicsAuthTypes.Office365
    );
    linkedService.servicePrincipalCredentialType = typeProperties.servicePrincipalCredentialType;
    ValueAdapter.toModel(linkedService, DynamicsLinkedService.SERVICE_PRINCIPLE_ID, typeProperties.servicePrincipalId);
    if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey) {
        parameterizableSecretBaseToModel(
            linkedService,
            DynamicsLinkedService.SERVICE_PRINCIPLE_KEY,
            typeProperties.servicePrincipalCredential,
            context,
            true
        );
    } else if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
        parameterizableSecretBaseToModel(
            linkedService,
            DynamicsLinkedService.SERVICE_PRINCIPLE_CERT,
            typeProperties.servicePrincipalCredential,
            context
        );
    } else if (typeProperties.credential) {
        linkedService.credential = ReferenceAdapter.toProxyModel(
            Metamodel.modelFor(Credential),
            typeProperties.credential,
            context
        );
    }

    return linkedService;
};
